import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import type { api } from "../components/utils/convex-api";

// ----------------- Schemas -----------------
const learningEnum = z.enum(["in_person", "remote"]);
type LearningPref = z.infer<typeof learningEnum>;

const subjectSchema = z.object({
  subjectId: z.string().min(1, "Subject is required"),
  qualificationId: z.string().optional(),
  examDetails: z.string().optional(),
});

// ✅ single schema that can flex between create/update
const baseChildSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  email: z.string().email(),
  yearGroupId: z.string().min(1, "Year group is required"),
  curriculumId: z.string().min(1, "Curriculum is required"),
  programmeId: z.string().optional(),
  subjects: z.array(subjectSchema).min(1, "At least one subject is required"),
  learningPreferences: z
    .array(learningEnum)
    .min(1, "At least one learning preference is required"),
  additionalLearningNeeds: z.string().optional(),
  school: z.string().optional(),
  imageId: z.string().optional(),
});

export type ChildFormInput = z.infer<typeof baseChildSchema>;

type UseChildFormOpts = {
  initialData:
    | typeof api.parent.manageChildren.queries.getChild._returnType
    | null;
};

// ----------------- Hook -----------------
export function useChildForm({ initialData }: UseChildFormOpts) {
  const form = useForm<ChildFormInput>({
    resolver: zodResolver(baseChildSchema),
    defaultValues: {
      firstName: "",
      email: "",
      yearGroupId: "",
      curriculumId: "",
      subjects: [],
      learningPreferences: [],
      additionalLearningNeeds: "",
      imageId: undefined,
      school: "",
      ...initialData,
      programmeId: initialData?.programmeId ?? "",
    },
  });

  const subjectsArray = useFieldArray({
    control: form.control,
    name: "subjects",
  });

  // Helpers for learning preferences
  const learningOptions = ["in_person", "remote"] as const;
  const selectedPrefs = form.watch("learningPreferences") ?? [];

  const isPrefChecked = (p: LearningPref) => selectedPrefs.includes(p);
  const togglePreference = (p: LearningPref) => {
    const next = isPrefChecked(p)
      ? selectedPrefs.filter((x) => x !== p)
      : [...selectedPrefs, p];
    form.setValue("learningPreferences", next as LearningPref[], {
      shouldValidate: true,
    });
  };

  return {
    ...form,
    // arrays
    subjectFields: subjectsArray.fields,
    addSubject: (v?: z.infer<typeof subjectSchema>) =>
      subjectsArray.append(
        v ?? { subjectId: "", qualificationId: "", examDetails: "" }
      ),
    removeSubject: subjectsArray.remove,
    // prefs
    learningOptions,
    isPrefChecked,
    togglePreference,
  };
}
